import * as types from '../mutation-types';
import {
  StudioEmailRecipientResponse,
  StudioEmailResponse,
  StudioMeetingResponse,
  StudioMeetingTypeResponse,
} from '@/core/typings/globals';
import api from '../../api/modules/marketing';
import { Module } from 'vuex';

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface State {
  meetingTypeCategories: any[];
}

const initialState: State = {
  meetingTypeCategories: [],
};

const marketingModule: Module<State, any> = {
  namespaced: true,
  state: initialState,
  getters: {
    meetingTypeCategories: state => state.meetingTypeCategories,
  },
  actions: {
    async loadMeetingTypes({
      commit,
    }): Promise<PaginatedResponse<StudioMeetingTypeResponse>> {
      return await api.getMeetingTypes();
    },
    async getMeetingType({ commit }, id): Promise<StudioMeetingTypeResponse> {
      return await api.getMeetingType(id);
    },
    async getPublicMeetingType(
      { commit },
      { studioURL, slug }
    ): Promise<StudioMeetingTypeResponse> {
      return await api.getPublicMeetingType(studioURL, slug);
    },
    async getPublicMeeting(
      { commit },
      { studioURL, meetingUniqueId }
    ): Promise<StudioMeetingResponse> {
      return await api.getPublicMeeting(studioURL, meetingUniqueId);
    },
    async cancelPublicMeeting(
      { commit },
      { studioURL, meetingUniqueId }
    ): Promise<any> {
      return await api.cancelPublicMeeting(studioURL, meetingUniqueId);
    },
    async reschedulePublicMeeting(
      { commit },
      { studioURL, meetingUniqueId, newStartTime, timezone }
    ): Promise<any> {
      return await api.reschedulePublicMeeting(
        studioURL,
        meetingUniqueId,
        newStartTime,
        timezone
      );
    },
    async getMeetingTypesCategories({ commit }): Promise<any> {
      const result = await api.getMeetingTypesCategories();
      commit(types.SET_MEETING_TYPE_CATEGORIES, result);
      return result;
    },
    async getPublicMeetingTypeTimeSlots(
      { commit },
      { studioURL, slug, timezone, startDate, endDate }
    ): Promise<StudioMeetingTypeResponse> {
      return await api.getPublicMeetingTypeTimeSlots(
        studioURL,
        slug,
        timezone,
        startDate,
        endDate
      );
    },
    async bookPublicMeetingSlot(
      { commit },
      { studioURL, slug, slot, timezone, bookingFullName, bookingEmail, form }
    ): Promise<StudioMeetingResponse> {
      return await api.bookPublicMeetingSlot(
        studioURL,
        slug,
        slot,
        timezone,
        bookingFullName,
        bookingEmail,
        form
      );
    },
    async deleteMeetingType({ commit }, id): Promise<any> {
      return await api.deleteMeetingType(id);
    },
    async cancelMeeting({ commit }, id): Promise<any> {
      return await api.cancelMeeting(id);
    },
    async rescheduleMeeting(
      { commit },
      { id, newStartTime, timezone }
    ): Promise<any> {
      return await api.rescheduleMeeting(id, newStartTime, timezone);
    },
    async getMeetingTypeTimeSlots(
      { commit },
      { id, timezone, startDate, endDate }
    ): Promise<StudioMeetingTypeResponse> {
      return await api.getMeetingTypeTimeSlots(
        id,
        timezone,
        startDate,
        endDate
      );
    },
    async duplicateMeetingType({ commit }, id): Promise<any> {
      return await api.duplicateMeetingType(id);
    },
    async addMeetingType({ commit }, data): Promise<StudioMeetingTypeResponse> {
      return await api.addMeetingType(data);
    },
    async updateMeetingType(
      { commit },
      data
    ): Promise<StudioMeetingTypeResponse> {
      return await api.updateMeetingType(data.id, data);
    },
    async getUpcomingMeetings(
      { commit },
      { q, page }
    ): Promise<PaginatedResponse<StudioMeetingResponse>> {
      return await api.getMeetings(page, q, 'upcoming');
    },
    async getPastMeetings(
      { commit },
      { q, page }
    ): Promise<PaginatedResponse<StudioMeetingResponse>> {
      return await api.getMeetings(page, q, 'past');
    },
    async getCancelledMeetings(
      { commit },
      { q, page }
    ): Promise<PaginatedResponse<StudioMeetingResponse>> {
      return await api.getMeetings(page, q, 'cancelled');
    },
    async loadEmails(
      { commit },
      payload
    ): Promise<PaginatedResponse<StudioEmailResponse>> {
      const { page, q, status, startDate, endDate } = payload;
      return await api.getEmails(page, q, status, startDate, endDate);
    },
    async getEmailRecipients(
      { commit },
      payload
    ): Promise<PaginatedResponse<StudioEmailRecipientResponse>> {
      const { studioEmailId, page, q, opened, clicked, unsubscribed } = payload;
      return await api.getEmailRecipients(
        studioEmailId,
        page,
        q,
        opened,
        clicked,
        unsubscribed
      );
    },
    async getEmail({ commit }, id): Promise<StudioEmailResponse> {
      return await api.getEmail(id);
    },
    async updateEmail({ commit }, data): Promise<StudioEmailResponse> {
      return await api.updateEmail(data.id, data);
    },
    async addEmail({ commit }, data): Promise<StudioEmailResponse> {
      return await api.addEmail(data);
    },
    async deleteEmail({ commit }, id): Promise<any> {
      return await api.deleteEmail(id);
    },
    async duplicateEmail({ commit }, id): Promise<any> {
      return await api.duplicateEmail(id);
    },
    async sendEmail({ commit }, id): Promise<StudioEmailResponse> {
      return await api.sendEmail(id);
    },
    async sendTestEmail({ commit }, id): Promise<StudioEmailResponse> {
      return await api.sendTestEmail(id);
    },
    async getEmailPreview({ commit }, id): Promise<StudioEmailResponse> {
      return await api.getEmailPreview(id);
    },
    async getEmailAudience({ commit }, data): Promise<StudioEmailResponse> {
      return await api.getEmailAudience(data);
    },
    async exportSubscribedStudentsToMarketingEmails(_) {
      const data = await api.exportSubscribedStudentsToMarketingEmails();
      const blob = new Blob([data], { type: 'text/csv;charset=utf-8;' });
      saveAs(blob, `subscribed-marketing-students.csv`);
    },
  },
  mutations: {
    [types.SET_MEETING_TYPE_CATEGORIES](state, categories) {
      state.meetingTypeCategories = categories;
    },
  },
};

export default marketingModule;
